import { type ReactNode, useState } from 'react';

import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import {
  RiCloseLine,
  RiFullscreenExitLine,
  RiFullscreenLine,
} from '@remixicon/react';

import styles from './detailDialog.module.css';

export interface DetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  defaultFullScreen?: boolean;
}

export function DetailDialog({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  defaultFullScreen = false,
}: DetailDialogProps) {
  const [isFullScreen, setIsFullScreen] = useState(defaultFullScreen);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      className={styles.overlay}
      isDismissable
    >
      <Modal
        className={`${styles.dialog}${
          isFullScreen ? ` ${styles.dialogFullScreen}` : ''
        }`}
      >
        <Dialog
          aria-label={title}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            outline: 'none',
          }}
        >
          <div className={styles.header}>
            <div className={styles.headerText}>
              <p className={styles.title}>{title}</p>
              {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            </div>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setIsFullScreen(fs => !fs)}
              aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullScreen ? (
                <RiFullscreenExitLine size={16} />
              ) : (
                <RiFullscreenLine size={16} />
              )}
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
              aria-label="Close"
            >
              <RiCloseLine size={16} />
            </button>
          </div>
          <div className={styles.body}>{children}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
